import { MiniPrimaryButton } from "@mnk/frontend/components/ui/button/PrimaryButton";
import { useQuantityUpload } from "@mnk/frontend/hooks/useQuantityUpload";
import { trpc } from "@mnk/frontend/lib/trpc";
import type { Drawing } from "@mnk/shared/models/drawing";
import { type Quantity, XLSX_CONTENT_TYPE } from "@mnk/shared/models/quantity";
import AddIcon from "@mui/icons-material/Add";
import TrashIcon from "@mui/icons-material/Delete";
import { useEffect, useRef } from "react";

interface Props {
  projectId: string;
  drawings: Drawing[];
  selectedDrawing: Drawing | null;
  setShowSelectDrawingModal: (show: boolean) => void;
  setSelectedDrawing: (drawing: Drawing | null) => void;
  reloadDrawings: () => Promise<void>;
  setShowQuantityModal: (show: boolean) => void;
  setQuantity: (quantity: Quantity | null) => void;
}

export const LeftSideBar = ({
  projectId,
  drawings,
  selectedDrawing,
  setShowSelectDrawingModal,
  setSelectedDrawing,
  reloadDrawings,
  setShowQuantityModal,
  setQuantity,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: deleteProjectDrawing } = trpc.deleteProjectDrawing.useMutation({
    onSuccess: async () => {
      await reloadDrawings();
    },
  });

  const { mutate: createFlag } = trpc.createFlag.useMutation({
    onSuccess: async () => {
      await reloadDrawings();
      await refetchQuantity();
    },
    // createFlagはmutate（非await）なのでhandleFileChangeのtry/catchでは拾えない。
    // DB保存失敗をサイレントにせず、ここでエラー表示する。
    onError: () => setError("数量表データの保存に失敗しました"),
  });

  const { data: quantity, refetch: refetchQuantity } = trpc.getQuantityByProjectId.useQuery({
    projectId,
  });

  const { parse: parseQuantity, processing, error: quantityError, setError } = useQuantityUpload();

  const onDeleteDrawing = (drawing: Drawing) => {
    deleteProjectDrawing({ drawingId: drawing.id, projectId });
  };

  const { mutateAsync: getQuantityUploadUrl } = trpc.getQuantityUploadUrl.useMutation();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);

    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // エクセルファイルを解析（拡張子チェック含む）
      const data = await parseQuantity(file, projectId);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      // 解析成功後、xlsx本体をS3へアップロード（プリサインドPUT）してからデータ保存
      const { url } = await getQuantityUploadUrl({ projectId });
      const res = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": XLSX_CONTENT_TYPE },
      });
      if (!res.ok) {
        throw new Error(`数量表ファイルの保存に失敗しました (${res.status})`);
      }
      createFlag(data);
    } catch (error) {
      console.error("エクセルファイルの処理中にエラーが発生しました:", error);
      setError(error instanceof Error ? error.message : "エクセルファイルの処理に失敗しました");
    }
  };

  useEffect(() => {
    if (quantity) {
      setQuantity(quantity);
    }
  }, [quantity, setQuantity]);

  return (
    <div className="w-96 flex-shrink-0 p-4 flex flex-col gap-2 h-full bg-white rounded-lg shadow-md">
      <div className="flex flex-row gap-2 justify-between items-center">
        <h2 className="text-left text-2xl font-bold">数量表</h2>
        <input type="file" className="hidden" onChange={handleFileChange} ref={inputRef} />
        <MiniPrimaryButton onClick={() => inputRef.current?.click()}>
          追加
          <AddIcon className="w-2 h-2" />
        </MiniPrimaryButton>
      </div>
      <p className="text-md">
        {quantity && (
          <span className="text-black">
            ファイル名: {quantity.name}
            <button className="text-blue-500 cursor-pointer" type="button" onClick={() => setShowQuantityModal(true)}>
              （プレビュー）
            </button>
          </span>
        )}
        {processing && " (処理中...)"}
        {quantityError && <span className="text-red-500"> エラー: {quantityError}</span>}
      </p>

      <div className="flex flex-row gap-2 justify-between items-center mt-8">
        <h2 className="text-left text-2xl font-bold">図面一覧</h2>
        <MiniPrimaryButton onClick={() => setShowSelectDrawingModal(true)}>
          追加
          <AddIcon className="text-xs" />
        </MiniPrimaryButton>
      </div>
      <table className="border border-gray-300">
        <thead>
          <tr>
            <th className="text-left text-sm font-normal bg-gray-200 p-2">名前</th>
            <th className="text-left text-sm font-normal bg-gray-200 p-2">開始</th>
            <th className="text-left text-sm font-normal bg-gray-200 p-2">終了</th>
            <th className="text-left text-sm font-normal bg-gray-200 p-2">-</th>
          </tr>
        </thead>
        <tbody>
          {drawings.map((drawing) => (
            <tr
              key={drawing.id}
              className={`cursor-pointer border-t border-gray-300 ${selectedDrawing?.id === drawing.id ? "bg-blue-100" : ""}`}
              onClick={() => setSelectedDrawing(drawing)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSelectedDrawing(drawing);
                }
              }}
            >
              <td className="text-left text-sm font-normal p-2">{drawing.name}</td>
              <td className="text-left text-sm font-normal p-2">{drawing.startKiloPost}</td>
              <td className="text-left text-sm font-normal p-2">{drawing.endKiloPost}</td>
              <td className="text-left text-sm font-normal p-2">
                <TrashIcon
                  className="w-2 h-2 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteDrawing(drawing);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
